import { deleteSingleSaleThunk, fetchSingleSaleDetailThunk, fetchSingleSalesThunk } from '@spike/single-sales-action';
import { SingleSaleDetail, SingleSalesFilterStatus, SingleSalesPage } from '@spike/single-sale-model';
import { Period } from '@spike/model';
import { showError } from '@spike/notifications-action';
import Checkout from 'components/Checkout2/Checkout';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './Sales.module.scss';
import SaleDetailDrawer from './Detail/SaleDetailDrawer';
import SalesHeader from './Header/SalesHeader';
import SalesTable from './Table/SalesTable';
import { SingleSale } from '../SingleSale/SingleSale';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

export type SalesStatusFilter = SingleSalesFilterStatus;

interface SalesProps {
    onMakeAnotherSale?: () => void;
}

export const Sales: FunctionComponent<SalesProps> = ({ onMakeAnotherSale }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const organizationMarketplaceIds = useMemo(
        () => organizationMarketplaces.map(marketplace => marketplace.id),
        [organizationMarketplaces]
    );

    const page = useSelector<RootState, SingleSalesPage | undefined>(state => state.singleSales.singleSalesPage);
    const loading = useSelector<RootState, boolean>(state => state.singleSales.loading);
    const saleDetail = useSelector<RootState, SingleSaleDetail | undefined>(
        state => state.singleSales.singleSaleDetail
    );
    const detailLoading = useSelector<RootState, boolean>(state => state.singleSales.detailLoading);
    const deleteLoading = useSelector<RootState, boolean>(state => state.singleSales.deleteLoading);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [status, setStatus] = useState<SalesStatusFilter>(SingleSalesFilterStatus.All);
    const [clientSearch, setClientSearch] = useState('');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() => organizationMarketplaceIds);
    const [selectedSaleId, setSelectedSaleId] = useState<number | null>(null);
    const [checkoutOrderId, setCheckoutOrderId] = useState<number | null>(null);
    const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
    const [editingSaleNumber, setEditingSaleNumber] = useState<number | null>(null);
    const [refreshAfterDelete, setRefreshAfterDelete] = useState(false);
    const [deleteStarted, setDeleteStarted] = useState(false);
    const [period, setPeriod] = useState<Period>(() => {
        const today = moment().tz(timeZone);

        return {
            from: today.clone().startOf('day'),
            to: today.clone().endOf('day')
        };
    });
    const [optionSelected, setOptionSelected] = useState<string | undefined>('Today');
    const debouncedClientSearch = useDebounce(clientSearch.trim(), 500);
    const marketplaceIdsForRequest = marketplaceIds.length > 0 ? marketplaceIds : organizationMarketplaceIds;

    useEffect(() => {
        if (organizationMarketplaceIds.length === 0) {
            return;
        }

        if (debouncedClientSearch.length > 0 && debouncedClientSearch.length < 3) {
            return;
        }

        dispatch(
            fetchSingleSalesThunk(apiClientWrapper, {
                marketplaceIds: marketplaceIdsForRequest,
                status,
                clientSearch: debouncedClientSearch || undefined,
                from: period.from.format('YYYYMMDD'),
                to: period.to.format('YYYYMMDD'),
                page: pageNumber,
                size: pageSize
            })
        );
    }, [
        debouncedClientSearch,
        marketplaceIdsForRequest,
        organizationMarketplaceIds.length,
        pageNumber,
        pageSize,
        period,
        status
    ]);

    const changeStatusHandler = (nextStatus: SalesStatusFilter) => {
        setPageNumber(1);
        setStatus(nextStatus);
    };

    const changeClientSearchHandler = (value: string) => {
        setPageNumber(1);
        setClientSearch(value);
    };

    const changeMarketplaceIdsHandler = (nextMarketplaceIds: Array<number>) => {
        setPageNumber(1);
        setMarketplaceIds(nextMarketplaceIds);
    };

    const changePeriodHandler = (nextPeriod: Period) => {
        setPageNumber(1);
        setPeriod(nextPeriod);
    };

    const changeOptionSelectedHandler = (nextOptionSelected?: string) => {
        setOptionSelected(nextOptionSelected);
    };

    const selectedSaleDetail = saleDetail?.id === selectedSaleId ? saleDetail : undefined;

    const fetchSales = () => {
        dispatch(
            fetchSingleSalesThunk(apiClientWrapper, {
                marketplaceIds: marketplaceIdsForRequest,
                status,
                clientSearch: debouncedClientSearch || undefined,
                from: period.from.format('YYYYMMDD'),
                to: period.to.format('YYYYMMDD'),
                page: pageNumber,
                size: pageSize
            })
        );
    };

    const selectSaleHandler = (saleId: number) => {
        setSelectedSaleId(saleId);
        dispatch(fetchSingleSaleDetailThunk(apiClientWrapper, saleId));
    };

    const closeDetailHandler = () => {
        setSelectedSaleId(null);
    };

    const deleteSaleHandler = () => {
        if (!selectedSaleId) return;

        dispatch(deleteSingleSaleThunk(apiClientWrapper, selectedSaleId));
        setSelectedSaleId(null);
        setRefreshAfterDelete(true);
        setDeleteStarted(false);
    };

    const collectPaymentHandler = () => {
        if (!selectedSaleDetail?.orderId) {
            dispatch(showError('Unable to open checkout for this sale. The sale detail does not include an order id.'));
            return;
        }

        setCheckoutOrderId(selectedSaleDetail.orderId);
        setSelectedSaleId(null);
    };

    const editSaleHandler = () => {
        if (!selectedSaleDetail?.orderId) {
            dispatch(showError('Unable to edit this sale. The sale detail does not include an order id.'));
            return;
        }

        setEditingOrderId(selectedSaleDetail.orderId);
        setEditingSaleNumber(selectedSaleDetail.saleNumber);
        setSelectedSaleId(null);
    };

    const closeCheckoutHandler = () => {
        setCheckoutOrderId(null);
    };

    const checkoutPaidHandler = () => {
        setSelectedSaleId(null);
        fetchSales();
    };

    const checkoutDoneHandler = () => {
        setCheckoutOrderId(null);
        fetchSales();
    };

    const closeEditHandler = () => {
        setEditingOrderId(null);
        setEditingSaleNumber(null);
        fetchSales();
    };

    useEffect(() => {
        if (!refreshAfterDelete) {
            return;
        }

        if (deleteLoading) {
            setDeleteStarted(true);
            return;
        }

        if (!deleteStarted) {
            return;
        }

        setRefreshAfterDelete(false);
        setDeleteStarted(false);
        fetchSales();
    }, [deleteLoading, deleteStarted, refreshAfterDelete]);

    if (editingOrderId) {
        return (
            <SingleSale
                editOrderId={editingOrderId}
                editSaleNumber={editingSaleNumber ?? undefined}
                onCancelEdit={closeEditHandler}
                onEditSaved={closeEditHandler}
                onMakeAnotherSale={onMakeAnotherSale}
            />
        );
    }

    return (
        <section className={styles.root}>
            <SalesHeader
                status={status}
                clientSearch={clientSearch}
                marketplaceIds={marketplaceIds}
                totalRecords={page?.pagination.totalRecords}
                loading={loading}
                period={period}
                optionSelected={optionSelected}
                onChangeStatus={changeStatusHandler}
                onChangeClientSearch={changeClientSearchHandler}
                onChangeMarketplaceIds={changeMarketplaceIdsHandler}
                onChangePeriod={changePeriodHandler}
                onChangeOptionSelected={changeOptionSelectedHandler}
            />
            <SalesTable
                page={page}
                loading={loading}
                timeZone={timeZone}
                onSelectSale={selectSaleHandler}
                onPageChange={setPageNumber}
                onSizeChange={size => {
                    setPageNumber(1);
                    setPageSize(size);
                }}
            />
            {selectedSaleId && (
                <SaleDetailDrawer
                    sale={selectedSaleDetail}
                    loading={detailLoading}
                    deleting={deleteLoading}
                    timeZone={timeZone}
                    onClose={closeDetailHandler}
                    onDelete={deleteSaleHandler}
                    onCollectPayment={collectPaymentHandler}
                    onEdit={editSaleHandler}
                />
            )}
            {checkoutOrderId && (
                <Checkout
                    orderId={checkoutOrderId}
                    type="singlesale"
                    zIndex={10030}
                    onClose={closeCheckoutHandler}
                    onPaid={checkoutPaidHandler}
                    onPayByLinkSent={checkoutDoneHandler}
                />
            )}
        </section>
    );
};

export default Sales;
